import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  api_create_send_message__url,
  api_get_job_messages_url,
  api_submit_job_url,
  api_get_job_detail_url,
  api_update_job_url,
  api_download_file_url,
  api_download_zip_file_url,
  api_job_list_url,
  api_send_proof,
  api_get_proof_log,
  api_viewproof,
  api_proof_approve,
  api_proof_reject,

  api_get_sidebar_menu,
  api_add_proof_comments,
  api_assign_job,
} from "./jobApi";
import {
  parser_create_send_message,
  parser_get_job_messages,
  parser_submit_job,
  parser_get_job_detail,
  parser_update_job,
  parser_download_file,
  parser_download_zip_file,
  parser_job_list,
  parser_send_proof,
  parser_proof_log_table,
  parser_viewproof,
  parser_proof_approve,
  parser_proof_reject,

  parser_get_sidebar_menu,
  parser_add_proof_comments,
  parser_assign_job,
} from "./jobParser";
import { setCommonLoading } from "../user/userSlice";
import { isAuth, getLocalStorage } from "../../helpers/cookies";

export const create_send_message = createAsyncThunk(
  "create_send_message",
  async (payload) => {
    try {
      const {
        job_id,
        action_type,
        message,
        attachment,
        prev_stage,
        new_stage,
      } = payload;
      const formData = new FormData();
      if (typeof message === "string") {
        formData.append("message", message);
      }
      if (attachment) {
        formData.append("attachment", attachment);
      }
      if (prev_stage) {
        formData.append("prev_stage", prev_stage);
      }
      if (new_stage) {
        formData.append("new_stage", new_stage);
      }
      const auth = isAuth();
      const auth_user = parseInt(auth?.user_id || auth?.id);
      if (auth_user) {
        formData.append("auth_user", auth_user);
      }
      const response = await api_create_send_message__url(
        job_id,
        action_type,
        formData,
      );
      const data = parser_create_send_message(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  },
);

export const get_job_messages = createAsyncThunk(
  "get_job_messages",
  async (payload) => {
    try {
      const { job_id } = payload;
      const response = await api_get_job_messages_url(job_id);
      const data = parser_get_job_messages(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  },
);

export const submit_job = createAsyncThunk(
  "submit_job",
  async (payload, { dispatch, rejectWithValue }) => {
    const { company_id, process_id, job_data } = payload;
    let finalCompanyId = company_id || (isAuth() ? isAuth().company_id : null);
    let finalProcessId = process_id;
    if (!finalProcessId) {
      const stored = getLocalStorage("selected_process_tab");
      if (stored && !isNaN(Number(stored))) finalProcessId = Number(stored);
    }

    if (!finalProcessId) {
      return rejectWithValue("Process ID is required to submit a job. Please select a process tab first.");
    }

    const appendFiles = (key, files, formData) => {
      if (Array.isArray(files)) {
        files
          .filter((f) => f instanceof File)
          .forEach((file) => formData.append(key, file));
      }
    };

    try {
      dispatch(setCommonLoading(true));

      const formData = new FormData();

      const cleanForJson = (val) => {
        if (val instanceof File) return undefined;
        if (Array.isArray(val))
          return val.map(cleanForJson).filter((v) => v !== undefined);
        if (val !== null && typeof val === "object") {
          const res = {};
          for (let k in val) {
            const v = cleanForJson(val[k]);
            if (v !== undefined) res[k] = v;
          }
          return res;
        }
        return val;
      };

      // Don't send stage_id — backend auto-resolves based on assigned_to
      if (job_data.assigned_to) {
        formData.append("assigned_to", Array.isArray(job_data.assigned_to) ? JSON.stringify(job_data.assigned_to) : job_data.assigned_to);
      }
      formData.append("job_data", JSON.stringify(cleanForJson(job_data)));

      // Top-level files
      appendFiles("submitted_file_list", job_data.submitted_file, formData);

      // Job items files
      if (Array.isArray(job_data.job_items)) {
        job_data.job_items.forEach((item, index) => {
          appendFiles(
            `job_items[${index}][submitted_file][]`,
            item.submitted_file,
            formData,
          );
        });
      }

      const response = await api_submit_job_url(
        finalCompanyId,
        finalProcessId,
        formData,
      );
      const data = parser_submit_job(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  },
);

export const get_job_detail = createAsyncThunk(
  "get_job_detail",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const { job_id } = payload;
      const response = await api_get_job_detail_url(job_id);
      const data = parser_get_job_detail(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  },
);

export const get_job_list = createAsyncThunk(
  "get_job_list",
  async ({
    company_id,
    process_id,
    stage_id,
    skip = 0,
    limit = 10,
    qn,
    column,
    assigned_to,
  }) => {
    try {
      const response = await api_job_list_url(
        company_id,
        process_id,
        stage_id,
        skip,
        limit,
        qn,
        column,
        assigned_to,
      );
      return parser_job_list(response);
    } catch (error) {
      throw new Error(error);
    }
  },
);

export const update_job = createAsyncThunk(
  "update_job",
  async (payload, { dispatch, rejectWithValue }) => {
    const { job_id, job_data } = payload;

    const appendFiles = (key, files, formData) => {
      if (Array.isArray(files)) {
        files
          .filter((f) => f instanceof File)
          .forEach((file) => formData.append(key, file));
      }
    };

    try {
      dispatch(setCommonLoading(true));

      const formData = new FormData();

      const cleanForJson = (val) => {
        if (val instanceof File) return undefined;
        if (Array.isArray(val))
          return val.map(cleanForJson).filter((v) => v !== undefined);
        if (val !== null && typeof val === "object") {
          const res = {};
          for (let k in val) {
            const v = cleanForJson(val[k]);
            if (v !== undefined) res[k] = v;
          }
          return res;
        }
        return val;
      };

      if (job_data.hasOwnProperty("assigned_to")) {
        formData.append("assigned_to", Array.isArray(job_data.assigned_to) ? JSON.stringify(job_data.assigned_to) : (job_data.assigned_to ?? ""));
      } else if (job_data.assigned_to !== undefined) {
        formData.append("assigned_to", Array.isArray(job_data.assigned_to) ? JSON.stringify(job_data.assigned_to) : job_data.assigned_to);
      }
      formData.append("job_data", JSON.stringify(cleanForJson(job_data)));

      // Top-level files
      appendFiles("submitted_file_list", job_data.submitted_file, formData);
      appendFiles("completed_file_list", job_data.completed_file, formData);

      // Job items files
      if (Array.isArray(job_data.job_items)) {
        job_data.job_items.forEach((item, index) => {
          appendFiles(
            `job_items[${index}][submitted_file][]`,
            item.submitted_file,
            formData,
          );
          appendFiles(
            `job_items[${index}][completed_file][]`,
            item.completed_file,
            formData,
          );
        });
      }

      const response = await api_update_job_url(job_id, formData);
      const data = parser_update_job(response);
      dispatch(get_job_detail({ job_id }));
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  },
);

export const download_file = createAsyncThunk(
  "download_file",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const { job_id, file_key, d_type } = payload;
      const response = await api_download_file_url(job_id, file_key, d_type);
      const data = parser_download_file(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  },
);

export const download_zip_file = createAsyncThunk(
  "download_zip_file",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const { job_id, comp_file_list } = payload;
      const response = await api_download_zip_file_url(job_id, comp_file_list);
      const data = parser_download_zip_file(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  },
);

export const send_proof = createAsyncThunk(
  "send proof",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const response = await api_send_proof(payload);
      const data = parser_send_proof(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  },
);

export const proof_log_table = createAsyncThunk(
  "proof log table",
  async (payload) => {
    try {
      const response = await api_get_proof_log(payload);
      const data = await parser_proof_log_table(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  },
);

export const get_viewproof = createAsyncThunk(
  "get_viewproof",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const { proof_id } = payload;
      const response = await api_viewproof(proof_id);
      const data = parser_viewproof(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  },
);

export const proof_approve = createAsyncThunk(
  "proof approve",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const response = await api_proof_approve(payload);
      const data = await parser_proof_approve(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  },
);

export const proof_reject = createAsyncThunk(
  "proof reject",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const response = await api_proof_reject(payload);
      const data = await parser_proof_reject(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  },
);



export const get_sidebar_menu = createAsyncThunk(
  "get_sidebar_menu",
  async ({ process_id, qn, column }, { rejectWithValue }) => {
    try {
      const response = await api_get_sidebar_menu(process_id, qn, column);
      const data = parser_get_sidebar_menu(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const add_proof_comments = createAsyncThunk(
  "add_proof_comments",
  async ({ proof_id, proof_comments }, { rejectWithValue }) => {
    try {
      const response = await api_add_proof_comments(proof_id, proof_comments);
      const data = parser_add_proof_comments(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const assign_job = createAsyncThunk(
  "assign_job",
  async ({ job_id, assigned_to }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api_assign_job(job_id, { assigned_to });
      const data = parser_assign_job(response);
      // Refresh job detail after assignment
      if (job_id) dispatch(get_job_detail({ job_id }));
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const initialState = {
  job_id: null,
  is_job_loading: false,
  send_message_loading: false,
  job_data: {},
  job_messages: [],
  job_detail: [],
  tab_index: 0,
  prev_stage: null,
  new_stage: null,
  job_list: [],
  total_job_count: 0,
  page: 0,
  search_query: "",
  search_columns: {},
  table_columns: [],
  searchFilters: [],
  search_column: "",
  send_proof: null,
  proof_data: [],
  proof_log_data: [],
  proofLoading: false,
  is_reject_modal_open: false,
  reject_comment: "",

  sidebar_menus: [],

  // Assignment state
  assign_loading: false,
  assign_error: null,
  assign_success: null,
  // Filter: null = all, user_id = assigned to that user
  active_assigned_to_filter: null,
  is_assign_modal_open: false,
  assign_modal_job_id: null,
};

export const jobSlice = createSlice({
  name: "job",
  initialState,
  reducers: {
    setJobPage: (state, action) => {
      state.page = action.payload;
    },
    setJobData: (state, action) => {
      state.job_data = action.payload;
    },
    setTabIndex: (state, action) => {
      state.tab_index = action.payload;
    },
    setPrevStage: (state, action) => {
      state.prev_stage = action.payload;
    },
    setNewStage: (state, action) => {
      state.new_stage = action.payload;
    },
    clearJobData: (state) => {
      state.job_data = {};
    },
    setAction: (state, action) => {
      state.c_action = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.search_query = action.payload;
    },
    setSearchColumn: (state, action) => {
      state.search_column = action.payload;
    },
    setRejectModalOpen: (state, action) => {
      state.is_reject_modal_open = action.payload;
    },
    setRejectComment: (state, action) => {
      state.reject_comment = action.payload;
    },
    setSearchFilters: (state, action) => {
      state.searchFilters = action.payload; // array of { key, value, label }
    },
    addSearchFilter: (state, action) => {
      state.searchFilters.push(action.payload);
    },
    removeSearchFilter: (state, action) => {
      const { key, value } = action.payload;
      state.searchFilters = state.searchFilters.filter(
        (f) => f.key !== key || f.value !== value,
      );
    },
    clearSearchFilters: (state) => {
      state.searchFilters = [];
    },
    setJobId: (state, action) => {
      state.job_id = action.payload;
    },
    clearJobDetail: (state) => {
      state.job_detail = [];
    },
    setAssignedToFilter: (state, action) => {
      state.active_assigned_to_filter = action.payload;
    },
    setAssignModalOpen: (state, action) => {
      state.is_assign_modal_open = action.payload.open;
      state.assign_modal_job_id = action.payload.job_id || null;
    },
  },


  extraReducers: (builder) => {
    // create send message
    builder.addCase(create_send_message.pending, (state) => {
      state.send_message_loading = true;
    });
    builder.addCase(create_send_message.fulfilled, (state, action) => {
      state.send_message_loading = false;
      state.job_messages.unshift(action.payload.data);
    });
    builder.addCase(create_send_message.rejected, (state) => {
      state.send_message_loading = false;
    });

    // get job send messages
    builder.addCase(get_job_messages.pending, (state) => {
      state.send_message_loading = true;
    });
    builder.addCase(get_job_messages.fulfilled, (state, action) => {
      state.send_message_loading = false;
      state.job_messages = action.payload.data;
    });
    builder.addCase(get_job_messages.rejected, (state) => {
      state.send_message_loading = false;
    });

    // create job
    builder.addCase(submit_job.fulfilled, (state, action) => {
      state.job_data = action.payload.data;
    });

    // get job details
    builder.addCase(get_job_detail.pending, (state) => {
      state.is_job_loading = true;
    });
    builder.addCase(get_job_detail.fulfilled, (state, action) => {
      state.is_job_loading = false;
      state.job_detail = action.payload.data;
      state.job_id = action.payload.data.id;
    });
    builder.addCase(get_job_detail.rejected, (state) => {
      state.is_job_loading = false;
    });

    // update job
    builder.addCase(update_job.fulfilled, (state, action) => {
      state.job_data = action.payload.data;
    });

    // get job list
    builder.addCase(get_job_list.pending, (state) => {
      state.common_loading = true;
      state.job_list = [];
    });
    builder.addCase(get_job_list.fulfilled, (state, action) => {
      state.common_loading = false;
      state.job_list = action.payload.jobs || [];
      state.total_job_count = action.payload.total_count || 0;
      if (action.payload.search_columns) {
        state.search_columns = action.payload.search_columns;
      }
      if (action.payload.table_columns) {
        state.table_columns = action.payload.table_columns;
      }
    });
    builder.addCase(get_job_list.rejected, (state) => {
      state.common_loading = false;
      state.job_list = [];
    });
    // send proof
    builder.addCase(send_proof.fulfilled, (state, action) => {
      state.send_proof = action.payload.data;
    });
    // view proof
    builder.addCase(get_viewproof.pending, (state) => {
      state.proofLoading = true;
    });
    builder.addCase(get_viewproof.fulfilled, (state, action) => {
      state.proof_data = action.payload.data;
      state.proofLoading = false;
    });
    builder.addCase(get_viewproof.rejected, (state) => {
      state.proofLoading = false;
    });
    // proof log
    builder.addCase(proof_log_table.fulfilled, (state, action) => {
      state.proof_log_data = action.payload.data;
    });
    // proof approve
    builder.addCase(proof_approve.fulfilled, (state, action) => {
      state.proof_approve = action.payload.data;
    });
    // proof rejected
    builder.addCase(proof_reject.fulfilled, (state, action) => {
      state.proof_reject = action.payload.data;
    });

    builder.addCase(get_sidebar_menu.fulfilled, (state, action) => {
      state.sidebar_menus = action.payload.data;
    });

    // assign job
    builder.addCase(assign_job.pending, (state) => {
      state.assign_loading = true;
      state.assign_error = null;
      state.assign_success = null;
    });
    builder.addCase(assign_job.fulfilled, (state, action) => {
      state.assign_loading = false;
      state.assign_success = action.payload.data;
      state.is_assign_modal_open = false;
      state.assign_modal_job_id = null;
    });
    builder.addCase(assign_job.rejected, (state, action) => {
      state.assign_loading = false;
      state.assign_error = action.payload || "Assignment failed";
    });
  },
});

export const {
  setJobData,
  setTabIndex,
  setPrevStage,
  setNewStage,
  clearJobData,
  setJobPage,
  setAction,
  setSearchQuery,
  setSearchColumn,
  setRejectModalOpen,
  setRejectComment,
  setSearchFilters,
  addSearchFilter,
  removeSearchFilter,
  clearSearchFilters,
  clearJobDetail,
  setJobId,
  setAssignedToFilter,
  setAssignModalOpen,
} = jobSlice.actions;


export default jobSlice.reducer;
